import { useState } from 'react';
import { useTranslations } from 'next-intl';
import axios from 'axios';
import Link from 'next/link';
import Header from '@/components/header/Header';
import Footer from '@/components/footer/Footer';
import Input from '@/components/form-fields/Input';
import ButtonWithLoading from '@/components/form-fields/ButtonWithLoading';
import RateLimitError from '@/components/errors/RateLimitError';
import studioStore from '@/stores/studioStore';

export default function RequestPasswordReset() {
  const t = useTranslations('Auth');

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setIsRateLimited(false);

    try {
      const response = await fetch('/api/auth/reset-password/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.status === 429) {
        setIsRateLimited(true);
      } else if (data.success) {
        setSuccess(true);
      } else {
        setError(data.message || t('reset_password_error'));
      }
    } catch (err) {
      setError(t('reset_password_error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Header title={t('reset_password')} />
      <main className="py-36 min-h-screen login-register-background">
        <div className="container mx-auto">
          <div className={`login-register-content max-w-lg mx-auto px-6 py-7 overflow-hidden mt-8 ${studioStore.settings.login_register?.container === "stamp" ? "stamp-border" : ""}`}
            style={studioStore.settings.login_register?.container === "stamp" ? {
              backgroundImage: `radial-gradient(8px, transparent 98%, ${studioStore.settings.login_register?.container_background_color}),linear-gradient(${studioStore.settings.login_register?.container_background_color} 0px, ${studioStore.settings.login_register?.container_background_color} 0px)`,
            } : {
              backgroundColor: studioStore.settings.login_register?.container_background_color,
            }}
          >
            <div className="content p-5">
              <h2 className="text-2xl uppercase font-medium mb-1">{t('reset_password')}</h2>
              <p className="text-gray-600 mb-6 text-sm">{t('reset_password_description')}</p>
              {success ? (
                <div className="rounded-md bg-green-50 p-4">
                  <div className="flex">
                    <div className="shrink-0">
                      <svg
                        className="h-5 w-5 text-green-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ltr:ml-3 rtl:mr-3">
                      <p className="text-sm font-medium text-green-800">
                        {t('reset_password_email_sent')}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <form className="space-y-6" onSubmit={handleSubmit}>
                  <div className="space-y-2">
                    <Input
                      title={t('email')}
                      type="email"
                      name="email"
                      value={email}
                      handleChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  {isRateLimited && (
                    <div className="mt-4">
                      <RateLimitError />
                    </div>
                  )}
                  {error && !isRateLimited && (
                    <div className="mt-4 rounded-md bg-red-50 p-4">
                      <div className="flex">
                        <div className="shrink-0">
                          <svg
                            className="h-5 w-5 text-red-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="ltr:ml-3 rtl:mr-3">
                          <p className="text-sm font-medium text-red-800">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-4">
                    <ButtonWithLoading
                      isLoading={isLoading}
                      text={t('send_reset_link')}
                      loadingText={t('sending')}
                      type="submit"
                    />
                  </div>
                  <div className="text-sm text-center">
                    <Link
                      href="/login"
                      className="font-medium text-primary hover:text-primary-dark"
                    >
                      {t('back_to_login')}
                    </Link>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

export async function getStaticProps() {
  const { data } = await axios.get(`${process.env.NEXT_PUBLIC_WORDPRESS_SITE_URL}wp-json/beaver_next_wordpress/v1/settings`);
  const { data: studioData } = await axios.get(`${process.env.NEXT_PUBLIC_WORDPRESS_SITE_URL}wp-json/beaver_next_wordpress/v1/acf_studio`);

  let lang = 'en';

  if (data?.language) {
    lang = data.language.split('_')[0];
  }

  const messages = await import(`../../messages/${lang}.json`);

  return {
    props: {
      data: data || {},
      messages: messages.default,
      studioData: studioData || {},
    },
    revalidate: 1,
  };
}